import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY    = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR      = 'linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%)';
const FONT       = "'Plus Jakarta Sans', sans-serif";
const IMG_VERSION = 'v2';

// ─── Animations ───────────────────────────────────────────────────────────────
const bounceIntro = keyframes`
  0%, 100% { transform: scale(1); }
  40%       { transform: scale(1.15) translateY(-6px); }
  70%       { transform: scale(1.07) translateY(-3px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50%       { transform: translateY(-12px) rotate(5deg); }
`;
const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50%       { transform: translateY(-8px) rotate(-3deg); }
`;
const bouncePar = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25%       { transform: translateY(-20px) rotate(-5deg); }
  50%       { transform: translateY(-10px) rotate(5deg); }
  75%       { transform: translateY(-20px) rotate(-3deg); }
`;
const pulseAnim = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 6px 24px rgba(43,188,175,0.4); }
  50%       { transform: scale(1.06); box-shadow: 0 10px 36px rgba(43,188,175,0.6); }
`;
const pulseRed = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 6px 24px rgba(239,68,68,0.4); }
  50%       { transform: scale(1.06); box-shadow: 0 10px 36px rgba(239,68,68,0.6); }
`;

// ─── Intro ────────────────────────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR}; display: flex; flex-direction: column;
  align-items: center;
  overflow: hidden; font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px; display: flex; align-items: center;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; background: transparent; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #333; transition: color 0.2s;
  &:hover { color: ${PRIMARY}; }
  .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; min-height: 0;
`;
const FloatIcon = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  font-size: 26px; box-shadow: 0 4px 14px rgba(0,0,0,0.12);
  animation: ${p => (p.$delay ?? 0) % 2 === 0 ? float : floatDelay} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
`;
const CharCard = styled.div`
  font-size: clamp(80px, 22vw, 120px);
  animation: ${bounceIntro} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.15));
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease; font-family: ${FONT};
`;
const Handle = styled.div`width: 40px; height: 4px; background: #e0e0e0; border-radius: 99px; margin: 0 auto 18px;`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900; margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 20px;`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff; font-size: 17px; font-weight: 800;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4); font-family: ${FONT};
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

// ─── Game ─────────────────────────────────────────────────────────────────────
const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: ${BG_GR}; font-family: ${FONT};
`;
const GameNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.12);
  padding: 10px 16px; display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`font-size: 17px; font-weight: 800; color: #1a2a2a;`;
const GameBody = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; padding: 24px 16px; gap: 20px;
  max-width: 480px; margin: 0 auto; width: 100%;
`;
const StatusPill = styled.div<{ $status: 'idle' | 'listening' | 'speaking' }>`
  padding: 8px 20px; border-radius: 99px;
  background: ${p => p.$status === 'listening' ? 'rgba(239,68,68,0.12)' : p.$status === 'speaking' ? 'rgba(34,197,94,0.12)' : 'rgba(45,188,175,0.08)'};
  color: ${p => p.$status === 'listening' ? '#dc2626' : p.$status === 'speaking' ? '#16a34a' : '#94a3b8'};
  font-size: 14px; font-weight: 700;
`;
const ParrotWrap = styled.div<{ $isPlaying: boolean }>`
  font-size: clamp(100px, 28vw, 160px); cursor: pointer;
  animation: ${p => p.$isPlaying ? bouncePar : bounceIntro} ${p => p.$isPlaying ? '0.5s' : '3s'} ease-in-out infinite;
  filter: drop-shadow(0 8px 24px rgba(0,0,0,0.15));
  transition: filter 0.2s;
  &:hover { filter: drop-shadow(0 12px 32px rgba(0,0,0,0.22)); }
`;
const Bubble = styled.div<{ $visible: boolean }>`
  background: #fff; border-radius: 20px; padding: 16px 24px;
  width: 100%; min-height: 60px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  opacity: ${p => p.$visible ? 1 : 0.4}; transition: opacity 0.3s;
  font-size: 18px; font-weight: 600; color: #1a2a2a;
  text-align: center; position: relative;
  &::before {
    content: ''; position: absolute; top: -14px; left: 50%;
    transform: translateX(-50%);
    border: 14px solid transparent;
    border-bottom-color: #fff; border-top: none;
  }
`;
const BigMicBtn = styled.button<{ $isListening: boolean }>`
  width: 100%; padding: 20px; border-radius: 99px; border: none;
  background: ${p => p.$isListening ? 'linear-gradient(135deg, #ef4444, #dc2626)' : PRIMARY_GR};
  color: #fff; font-size: 20px; font-weight: 800;
  cursor: pointer; font-family: ${FONT};
  animation: ${p => p.$isListening ? pulseRed : 'none'} 1s ease-in-out infinite;
  box-shadow: ${p => p.$isListening ? '0 6px 24px rgba(239,68,68,0.4)' : '0 6px 24px rgba(43,188,175,0.4)'};
  transition: background 0.3s;
  &:disabled { opacity: 0.5; cursor: default; animation: none; }
`;
const ActionRow = styled.div`display: flex; gap: 10px; width: 100%;`;
const SmallBtn = styled.button<{ $danger?: boolean }>`
  flex: 1; padding: 14px; border-radius: 99px;
  border: 2px solid ${p => p.$danger ? 'rgba(239,68,68,0.3)' : '#e0e0e0'};
  background: ${p => p.$danger ? 'rgba(239,68,68,0.06)' : '#fff'};
  color: ${p => p.$danger ? '#dc2626' : '#555'};
  font-size: 14px; font-weight: 700; cursor: pointer; font-family: ${FONT};
  transition: all 0.2s;
  &:disabled { opacity: 0.4; cursor: default; }
  &:hover:not(:disabled) { border-color: ${p => p.$danger ? '#dc2626' : PRIMARY}; }
`;
const TipCard = styled.div`
  background: rgba(255,255,255,0.7); border-radius: 16px; padding: 16px;
  width: 100%; font-size: 13px; color: #7a8a8a; line-height: 1.8;
`;

declare global {
  interface Window { SpeechRecognition: any; webkitSpeechRecognition: any; }
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const ParrotGame: React.FC = () => {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [userText, setUserText]   = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isPlaying, setIsPlaying]     = useState(false);
  const [hasRecording, setHasRecording] = useState(false);

  const mediaRecorderRef    = useRef<MediaRecorder | null>(null);
  const audioChunksRef      = useRef<Blob[]>([]);
  const audioRef            = useRef<HTMLAudioElement | null>(null);
  const streamRef           = useRef<MediaStream | null>(null);
  const recognitionRef      = useRef<any>(null);
  const silenceTimeoutRef   = useRef<NodeJS.Timeout | null>(null);
  const maxRecordTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef     = useRef<AudioContext | null>(null);
  const analyserRef         = useRef<AnalyserNode | null>(null);
  const playbackSourceRef   = useRef<AudioBufferSourceNode | null>(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      recognitionRef.current = new SR();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'id-ID';
      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setUserText(transcript);
        stopListening();
        setHasRecording(true);
        setTimeout(() => playRecording(), 500);
      };
      recognitionRef.current.onspeechend = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopListening(); setHasRecording(true);
          setTimeout(() => playRecording(), 500);
        }
      };
      recognitionRef.current.onerror = () => {};
    }
    return () => {
      recognitionRef.current?.abort();
      streamRef.current?.getTracks().forEach(t => t.stop());
      audioRef.current?.pause();
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      if (maxRecordTimeoutRef.current) clearTimeout(maxRecordTimeoutRef.current);
      audioContextRef.current?.close();
    };
  }, []);

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch (_) {}
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach(t => t.stop());
    if (silenceTimeoutRef.current) { clearTimeout(silenceTimeoutRef.current); silenceTimeoutRef.current = null; }
    if (maxRecordTimeoutRef.current) { clearTimeout(maxRecordTimeoutRef.current); maxRecordTimeoutRef.current = null; }
    setIsListening(false);
  };

  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = []; setUserText(''); setHasRecording(false);
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      if (maxRecordTimeoutRef.current) clearTimeout(maxRecordTimeoutRef.current);

      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const src = audioContextRef.current.createMediaStreamSource(stream);
      src.connect(analyserRef.current);
      analyserRef.current.fftSize = 2048;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorderRef.current.onstop = () => { setHasRecording(true); setTimeout(() => playRecording(), 500); };
      mediaRecorderRef.current.start();
      setIsListening(true);

      if (recognitionRef.current) { try { recognitionRef.current.start(); } catch (_) {} }

      maxRecordTimeoutRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopListening(); setHasRecording(true);
        }
      }, 3000);

      const checkAudioLevel = () => {
        if (!analyserRef.current) return;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        if (avg < 5) {
          if (!silenceTimeoutRef.current) {
            silenceTimeoutRef.current = setTimeout(() => {
              if (mediaRecorderRef.current?.state === 'recording') {
                stopListening(); setHasRecording(true);
              }
            }, 1000);
          }
        } else {
          if (silenceTimeoutRef.current) { clearTimeout(silenceTimeoutRef.current); silenceTimeoutRef.current = null; }
        }
        if (mediaRecorderRef.current?.state === 'recording') requestAnimationFrame(checkAudioLevel);
      };
      checkAudioLevel();
    } catch (_) {
      setIsListening(false);
    }
  };

  const playRecording = async () => {
    if (audioChunksRef.current.length === 0) return;
    if (playbackSourceRef.current) { try { playbackSourceRef.current.stop(); } catch (_) {} }
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const blob = new Blob(audioChunksRef.current);
      const buf = await blob.arrayBuffer();
      const audio = await ctx.decodeAudioData(buf);
      const src = ctx.createBufferSource();
      src.buffer = audio;
      src.playbackRate.value = 1.7; // Parrot pitch effect
      src.connect(ctx.destination);
      src.start();
      setIsPlaying(true);
      src.onended = () => { setIsPlaying(false); ctx.close(); };
      playbackSourceRef.current = src;
    } catch (_) { setIsPlaying(false); }
  };

  const getStatus = (): 'idle' | 'listening' | 'speaking' =>
    isListening ? 'listening' : isPlaying ? 'speaking' : 'idle';

  const statusText = isListening ? '🎤 Merekam...' : isPlaying ? '🔊 Mengulangi suara...' : '😴 Siap merekam';

  if (showIntro) {
    return (
      <IntroOverlay>
        <IntroWrapper>
        <TopNav><BackBtn onClick={() => navigate('/')}><span className="material-symbols-outlined">arrow_back</span></BackBtn></TopNav>
        <CharArea>
          <FloatIcon $top="16%" $left="8%" $delay={0}>🎤</FloatIcon>
          <FloatIcon $top="12%" $right="10%" $delay={1}>🔊</FloatIcon>
          <FloatIcon $bottom="22%" $left="10%" $delay={2}>🎵</FloatIcon>
          <FloatIcon $bottom="18%" $right="8%" $delay={3}>🎧</FloatIcon>
          <CharCard>
            <img src={`/assets/images/animals/animal_bird.png?${IMG_VERSION}`} alt="bird" style={{ width: 'clamp(90px, 22vw, 130px)', height: 'clamp(90px, 22vw, 130px)', objectFit: 'contain' }} />
          </CharCard>
        </CharArea>
        <InfoCard>
          <Handle />
          <IntroTitle>Tirukan <span>Kakaktua!</span></IntroTitle>
          <IntroDesc>Bicara ke mikrofon dan kakaktua ajaib akan menirukan suaramu dengan nada lucu! 🎙️</IntroDesc>
          <StartBtn onClick={() => setShowIntro(false)}>Mulai Bermain</StartBtn>
        </InfoCard>
        </IntroWrapper>
      </IntroOverlay>
    );
  }

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => setShowIntro(true)}><span className="material-symbols-outlined">arrow_back</span></BackBtn>
          <GameTitle>Tirukan Kakaktua</GameTitle>
        </NavLeft>
        <StatusPill $status={getStatus()}>{statusText}</StatusPill>
      </GameNav>

      <GameBody>
        <ParrotWrap $isPlaying={isPlaying} onClick={playRecording} title="Klik untuk mengulang rekaman">
          🦜
        </ParrotWrap>

        <Bubble $visible={!!userText}>
          {userText || 'Silakan bicara, aku akan merekam dan melucu-lucuin suaramu! 🎤'}
        </Bubble>

        <BigMicBtn
          $isListening={isListening}
          onClick={startListening}
          disabled={isListening || isPlaying}
        >
          {isListening ? '🎤 Sedang Merekam...' : '🎤 Bicara Sekarang!'}
        </BigMicBtn>

        <ActionRow>
          <SmallBtn $danger disabled={!isListening} onClick={stopListening}>
            ⏹ Stop
          </SmallBtn>
          <SmallBtn disabled={!hasRecording || isPlaying} onClick={playRecording}>
            🔊 Ulang Suara
          </SmallBtn>
        </ActionRow>

        <TipCard>
          💡 <strong>Tips:</strong><br />
          • Klik "Bicara" untuk mulai merekam<br />
          • Bicara dengan jelas (maks 3 detik)<br />
          • Rekaman otomatis berhenti & diputar kembali<br />
          • Klik kakaktua 🦜 untuk mengulangi rekaman
        </TipCard>
      </GameBody>
    </GameWrap>
  );
};

export default ParrotGame;
